import React from 'react';
import { Card } from '../components/ui';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
    <div className="max-w-4xl mx-auto space-y-6">
      <Card className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Statement</h1>
            <p className="text-lg text-gray-600 font-medium">AVI Platform by Athletiverse, Inc.</p>
            <p className="text-sm text-gray-500 mt-2">Effective Date: August 31, 2025</p>
            <p className="text-sm text-gray-500">Last Updated: May 14, 2026</p>
          </div>

          <div className="prose prose-slate max-w-none text-sm">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. INTRODUCTION</h2>
              <p className="text-gray-700 mb-4">
                Athletiverse, Inc. ("we," "us," "our") operates the AVI platform ("Platform"). This Privacy Statement explains how we collect, use, disclose, and protect information when you use our Platform.
              </p>
              <p className="text-gray-700 mb-4">
                By using the Platform, you agree to the collection and use of information in accordance with this Privacy Statement. Our use of data obtained through Meta's platforms (including Facebook and Instagram) is further governed by the <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta Platform Terms</a> and <a href="https://developers.facebook.com/devpolicy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta Developer Policies</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. INFORMATION WE COLLECT</h2>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.1 Account Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Name and email address</li>
                <li>Company name and details</li>
                <li>Billing information (processed by Stripe)</li>
                <li>Account credentials</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.2 Platform Usage Data</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Social media account connections</li>
                <li>Post performance metrics</li>
                <li>Engagement analytics</li>
                <li>Click-through data</li>
                <li>EMV calculations</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.3 Integrated Platform Data</h3>
              <p className="text-gray-700 mb-2">We collect data from your connected accounts on:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Instagram</li>
                <li>LinkedIn</li>
                <li>Twitter/X</li>
                <li>YouTube</li>
                <li>BeeHiiv newsletters</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.4 Instagram and Facebook Graph API Data</h3>
              <p className="text-gray-700 mb-2">
                When you connect an Instagram Professional (Business or Creator) account via our Enhanced API integration, we collect and store the following data through the Meta Graph API:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>OAuth credentials</strong>: Facebook Page Access Tokens and Instagram Business Account IDs, used solely to make authorized API calls on your behalf</li>
                <li><strong>Post-level metrics</strong>: views/impressions, reach, likes, shares, saves — retrieved per individual post to calculate Earned Media Value (EMV)</li>
                <li><strong>Audience demographics</strong> (where Advanced Access is granted): aggregated follower data by age range, gender, city, and country — used for analytics reporting only</li>
                <li><strong>Media metadata</strong>: post timestamps, shortcodes, and media type (image, video, carousel)</li>
              </ul>
              <p className="text-gray-700 mb-4">
                This data is used exclusively to provide the analytics features of the Platform. We do not use Instagram or Facebook data to build advertising profiles, nor do we share it with third parties except as described in Section 4.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.5 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>IP addresses</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Usage patterns and preferences</li>
                <li>Login history</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">2.6 AI-Generated Insights</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Post performance analysis</li>
                <li>Content recommendations</li>
                <li>Trend identification</li>
                <li>Competitive benchmarking results</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. HOW WE USE YOUR INFORMATION</h2>
              <p className="text-gray-700 mb-2">We use collected information to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide and improve Platform services</li>
                <li>Calculate EMV and performance metrics</li>
                <li>Generate AI-powered insights and recommendations</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send service-related communications</li>
                <li>Provide customer support</li>
                <li>Ensure Platform security and prevent fraud</li>
                <li>Comply with legal obligations</li>
                <li>Develop new features and services</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Data obtained through the Meta Graph API is used only for the purposes disclosed to you at the time of authorization and in accordance with Meta's Platform Terms. We do not use such data for purposes unrelated to the analytics services you have requested.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. INFORMATION SHARING</h2>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 We DO NOT Sell Your Data</h3>
              <p className="text-gray-700 mb-4">
                We do not sell, rent, or trade your personal or business data to third parties. Data obtained through the Meta Graph API is never sold or used for advertising purposes.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.2 Service Providers</h3>
              <p className="text-gray-700 mb-2">We share information with third-party service providers including:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Stripe</strong>: Payment processing</li>
                <li><strong>Microsoft Azure</strong>: Cloud hosting and data storage</li>
                <li><strong>Meta (Facebook/Instagram)</strong>: API integration for retrieving authorized analytics data</li>
                <li><strong>Anthropic (Claude)</strong>: AI-powered content analysis and insight generation. Post content and performance data may be processed by Anthropic's Claude models to generate recommendations. Anthropic does not retain this data for model training.</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.3 Analytics Partners</h3>
              <p className="text-gray-700 mb-4">
                We may share aggregated, anonymized data with partners to improve our services and develop industry insights. Instagram or Facebook user data is never included in such sharing.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.4 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose information when required by law, court order, or to protect our rights and safety.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">4.5 Business Transfers</h3>
              <p className="text-gray-700 mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the successor entity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. DATA STORAGE AND SECURITY</h2>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5.1 Storage Location</h3>
              <p className="text-gray-700 mb-4">
                Your data is stored on Microsoft Azure servers in the West Region of the United States.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5.2 Security Measures</h3>
              <p className="text-gray-700 mb-2">We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Encryption in transit and at rest</li>
                <li>Access controls and authentication</li>
                <li>Regular security audits</li>
                <li>Secure development practices</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5.3 Data Retention</h3>
              <p className="text-gray-700 mb-4">
                We retain your data for as long as your account is active or as needed to provide services. Upon account deletion or written request to info@athletiverse.io, we will remove your data within 30 days, except where retention is required by law.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">5.4 Instagram and Facebook Data Retention</h3>
              <p className="text-gray-700 mb-4">
                When you disconnect your Instagram account from the Platform, or when you revoke the Platform's access via your Facebook account settings, we will delete your stored OAuth credentials (Page Access Tokens and Instagram Business Account IDs) within 30 days. Historical analytics data (post metrics and aggregated audience demographics) that has already been processed may be retained in anonymized or aggregated form for service improvement purposes but will no longer be associated with your identity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. YOUR RIGHTS AND CHOICES</h2>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.1 Access and Portability</h3>
              <p className="text-gray-700 mb-4">
                You may request a copy of your personal data in a portable format.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.2 Correction</h3>
              <p className="text-gray-700 mb-4">
                You can update your account information through the Platform or by contacting support.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.3 Deletion</h3>
              <p className="text-gray-700 mb-4">
                You may request deletion of your account and associated data by emailing info@athletiverse.io. This includes any data collected via the Meta Graph API on your behalf.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.4 Facebook and Instagram Data Deletion</h3>
              <p className="text-gray-700 mb-2">
                You may request deletion of your Facebook and Instagram data held by Athletiverse at any time. Upon request, we will delete your OAuth credentials (Page Access Tokens and Instagram Business Account IDs) and remove the association between your Facebook identity and your AVI account within 30 days. Use any of the following methods:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Within AVI</strong>: Navigate to Settings → Social Accounts, locate your Instagram account, and click "Disconnect." This immediately revokes our access and queues your credentials for deletion.</li>
                <li><strong>Via Facebook</strong>: Go to your Facebook account → Settings &amp; Privacy → Settings → Apps and Websites, find "AVI by Athletiverse," and click Remove. We will be notified and will delete your associated data within 30 days.</li>
                <li><strong>Via email</strong>: Send a request to <a href="mailto:info@athletiverse.io" className="text-blue-600 hover:underline">info@athletiverse.io</a> with the subject line "Facebook Data Deletion Request." Include your registered email address. We will confirm deletion within 30 days.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Following deletion, aggregated or anonymized analytics data that cannot be linked back to your identity may be retained for service improvement purposes, as described in Section 5.4.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.5 Opt-Out</h3>
              <p className="text-gray-700 mb-4">
                You can opt-out of non-essential communications through your account settings.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">6.6 Do Not Track</h3>
              <p className="text-gray-700 mb-4">
                The Platform does not currently respond to Do Not Track browser signals.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. INTERNATIONAL DATA TRANSFERS</h2>
              <p className="text-gray-700 mb-4">
                We operate globally and may transfer your information to countries with different data protection laws. Where required, we rely on appropriate safeguards such as Standard Contractual Clauses approved by the European Commission. By using the Platform, you consent to these transfers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. COOKIES AND TRACKING TECHNOLOGIES</h2>
              <p className="text-gray-700 mb-2">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Maintain your session</li>
                <li>Remember your preferences</li>
                <li>Analyze Platform usage</li>
                <li>Improve user experience</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can manage cookie preferences through your browser settings, though some Platform features may not function properly without cookies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. CHILDREN'S PRIVACY</h2>
              <p className="text-gray-700 mb-4">
                You must be at least 13 years old to use this Platform. The Platform is designed for business use and is not directed at children under 13. We do not knowingly collect information from individuals under 13. If we discover such collection has occurred, we will promptly delete the information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. THIRD-PARTY LINKS</h2>
              <p className="text-gray-700 mb-4">
                The Platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. CALIFORNIA PRIVACY RIGHTS</h2>
              <p className="text-gray-700 mb-2">California residents have additional rights under the California Consumer Privacy Act (CCPA):</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Right to know what personal information is collected</li>
                <li>Right to know if personal information is sold or disclosed</li>
                <li>Right to refuse the sale of personal information</li>
                <li>Right to access personal information</li>
                <li>Right to equal service and non-discrimination</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, contact info@athletiverse.io.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. GDPR COMPLIANCE</h2>
              <p className="text-gray-700 mb-2">For users in the European Economic Area:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Legal Basis</strong>: We process data based on contract performance, legitimate interests, and consent</li>
                <li><strong>Data Controller</strong>: Athletiverse, Inc. is the data controller</li>
                <li><strong>Rights</strong>: You have rights to access, rectification, erasure, restriction, portability, and objection</li>
                <li><strong>Complaints</strong>: You may lodge complaints with your local data protection authority</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">13. META PLATFORM COMPLIANCE</h2>
              <p className="text-gray-700 mb-4">
                Athletiverse, Inc. complies with Meta's Platform Terms and Developer Policies in its use of the Instagram and Facebook Graph APIs. Data obtained from Meta's platforms is used only as permitted under those policies and only for the purposes described in this Privacy Statement. We do not transfer Meta platform data to third-party ad networks, data brokers, or any other advertising or monetization-related service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">14. CHANGES TO THIS PRIVACY STATEMENT</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Statement periodically. We will notify you of material changes via email or Platform notification. Continued use of the Platform following notice of changes constitutes your acceptance of the updated Privacy Statement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">15. CONTACT US</h2>
              <p className="text-gray-700 mb-4">For privacy-related questions or concerns:</p>

              <div className="bg-gray-50 p-4 rounded mb-4">
                <p className="text-gray-700 font-medium mb-2">Privacy Contact</p>
                <p className="text-gray-700">
                  Athletiverse, Inc.<br/>
                  8635 W. Sahara Ave #528<br/>
                  Las Vegas, NV 89117<br/>
                  Email: info@athletiverse.io
                </p>
              </div>

              <p className="text-gray-700">For general support: info@athletiverse.io</p>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-gray-700 font-medium text-center">
                By using the AVI Platform, you acknowledge that you have read and understood this Privacy Statement and agree to our data practices described herein.
              </p>
            </div>
          </div>
        </Card>
    </div>
    </div>
  );
};

export default PrivacyPage;
